import { http } from '@api/client';
import { http_v2 } from '@api/client';
import { paramterize, pickResponse, pickResult } from '@api/utils';
import { PersonMaster, PersonMergeResponse } from '@lib/person-management';
import { BaseResponse } from '@api/shared';
import {
  PersonManagementFilterParams,
  // personManagementSearchResult,
  PersonManagementResult,
  FilterResponse,
  SearchPersonManagementQuery,
  SearchPersonManagementResponse,
  PersonsAutoCompleteResult,
} from './types';
import { map } from 'rxjs/operators';

/**
 * Get latest deals or search and get the fitered deals.
 * @param query The query params to pass to the url.
 */
export function search(query: SearchPersonManagementQuery, vtr_id: number) {
  const params = paramterize(query);
  return http
    .get<SearchPersonManagementResponse>(`/person-master/search/${vtr_id}`, {
      params,
    })
    .pipe(pickResponse());
}

/**
 * Get the deal details from the server.
 * @param id The id of the deal to get
 */
export function get(vtr_id: string) {
  return http.get<PersonManagementResult>(`/people/${vtr_id}`).pipe(pickResult());
}

/**
 * Filter deals using dynamic operators.
 * @param option Query options
 */
export function filter({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}: PersonManagementFilterParams = {}) {
  const isPersonViewSort = (key: string) =>
    ['base_persons_count', 'contents_associated_count'].includes(key);

  const sort_person_view = sortBy
    .filter(({ id }) => isPersonViewSort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort = sortBy
    .filter(({ id }) => !isPersonViewSort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  /////

  const newFilters = filters; //updateFiltersForLinkedOperator(filters);
  const filtersMap: Record<string, string> = newFilters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' && f.id === 'person_provided_id'
              ? `$in|||${v
                  .split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${v.trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({
    page_size,
    page,
    sort,
    sort_person_view,
    // sort_content_title,
    // sort_client_name,
    // sort_person_name,
    ...filtersMap,
  });

  return http.get<FilterResponse>(`/people`, { params }).pipe(pickResponse());
}

export function person_dedupe_get({
  filters = [],
  pageSize: page_size = 10,
  page = 1,
  sortBy = [],
}) {
  const isPersonSort = (key: string) =>
    [
      // Removed deals_count, content_count, company_count from sorting
    ].includes(key);

  const sort = sortBy
    .filter(({ id }) => isPersonSort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const sort_person_details = sortBy
    .filter(({ id }) => !isPersonSort(id))
    .map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`)
    .join(';');

  const filtersMap: Record<string, string> = filters.reduce(
    (map, f) =>
      Object.assign(map, {
        [f.id]: Object.entries(f.value)
          .map(([op, v]) => {
            return op === '$bw'
              ? `$gte|||${v[0]};$lte|||${v[1]}`
              : op === '$in' &&
                (f.id === 'gender' ||
                  f.id === 'datasource_id' ||
                  f.id === 'role_id')
              ? `$in|||${(v as string
                  ).split(',')
                  .map(v => v.split('|||')[0])
                  .join(',')}`
              : `${op}|||${((v || '') as string).trim()}`;
          })
          .join(';'),
      }),
    {}
  );

  const params = paramterize({ page_size, page, sort, sort_person_details, ...filtersMap});

  return http.get<FilterResponse>('/people-dedupe', { params }).pipe(
    pickResponse(),
    map(res => ({
      ...res,
      data: {
        ...res.data,
        data: (Array.isArray(res?.data?.data) ? res.data.data : []).map(val => ({
          ...val,
        })),
      },
    }))
  );
}

/**
 * Updates the given deal.
 * @param deal The deal to update
 */
export function update(personData: Partial<PersonMaster>) {
  const name = [personData['firstname']?.trim(), personData['middlename']?.trim(), personData['lastname']?.trim()];
  personData['name'] = name.filter(x => typeof x === 'string' && x.length > 0).join(" ");
  personData['person_id'] = personData['person_id'] ? +personData['person_id'] : null;       //Changed person_id and hubspot_id datatype to number as per VSDI-10377
  personData['hubspot_id'] = personData['hubspot_id'] ? +personData['hubspot_id'] : null;
  personData['popularity'] = 1;
  
  personData['gender'] = personData['gender'] ? personData['gender'] : null;
  personData['source_urls'] = personData['source_urls'] ? personData['source_urls']?.join(',') : "";
  
  // Ensure role_id is included in the payload
  personData['role_id'] = personData['role_id'] || null;
  
  // Remove fields that should not be sent in POST requests
  const { tmdb_person_id, imdb_person_id, gvd_person_id, deals_count, content_count, company_count, ...cleanedPersonData } = personData;
  
  return http.put<PersonMaster>(`/people/${cleanedPersonData.vtr_id}`, cleanedPersonData).pipe(pickResponse());
}

/**
 * Deletes the given deal.
 * @param deal The deal id or the actual deal.
 */
export function remove(personData: Partial<PersonMaster>) {
  return http
    .delete<PersonMaster>(
      `/people/${personData.vtr_id}`
      // , {
      //   data: { vtr_id: relationData.vtr_id,
      //     delete_reason: relationData.delete_reason },
      // }
    )
    .pipe(pickResponse());
}

/**
 * Creates the given deal.
 * @param deal The deal to create
 */
 export function create(personData: Partial<PersonMaster>) {
  const name = [personData['firstname']?.trim(), personData['middlename']?.trim(), personData['lastname']?.trim()];
  // personData['name'] =  name.join(' ');
  personData['name'] = name.filter(x => typeof x === 'string' && x.length > 0).join(" ");
  personData['person_id'] = personData['person_id'] ? +personData['person_id'] : null;       //Changed person_id and hubspot_id datatype to number as per VSDI-10377
  personData['hubspot_id'] = personData['hubspot_id'] ? +personData['hubspot_id'] : null;
  personData['popularity'] = 1;
  personData['gender'] = personData['gender'] ? personData['gender'] : null;
  personData['source_urls'] = personData['source_urls'] ? personData['source_urls']?.join(",") : "";
  
  // Ensure role_id is included in the payload
  personData['role_id'] = personData['role_id'] || null;
  
  // Remove fields that should not be sent in POST requests
  const { tmdb_person_id, imdb_person_id, gvd_person_id, deals_count, content_count, company_count, ...cleanedPersonData } = personData;
  
  return http.post<PersonManagementResult>(`/people`, cleanedPersonData).pipe(pickResponse());
}

/**
 * Gets autocomplete for persons
 */
export function autoCompleteForPersons() {
  return http.get<PersonsAutoCompleteResult>(`/company-persons-master-auto-complete`).pipe(
    pickResult(),
    map(x => {
      const allAutoCompletePersons = x['autocomplete-persons'];
      const mappedPersons = allAutoCompletePersons.map((x, index) => ({ id: index, person: x }));
      return mappedPersons;
    })
  );
}

export function searchAttributeMaster(page = 1, pageSize = 100) {
  return http
    .get('/people/attribute-master/searches', {
      params: paramterize({ page, page_size: pageSize }),
    })
    .pipe(pickResponse());
}

export function merge(person: any, toMerge: string[]) {
  // Ensure role_id is included in the payload
  person['role_id'] = person['role_id'] || null;
  //name field is update form firstname,middlename and lastname
  const name = [person['firstname']?.trim(), person['middlename']?.trim(), person['lastname']?.trim()];
  person['name'] = name.filter(x => typeof x === 'string' && x.length > 0).join(" ");
  //console.log("person:",person , name , person['name'])

  // Remove fields that should not be sent in POST requests
  const { tmdb_person_id, imdb_person_id, gvd_person_id, deals_count, content_count, company_count, deal_count, ...cleanedPerson } = person;
  
  return http
    .post<BaseResponse<PersonMergeResponse>>('/people-dedupe', {
      new_person_data: cleanedPerson,
      person_ids: toMerge,
    })
    .pipe(pickResult());
}
